import { jsx } from 'react/jsx-runtime';
import { createContext, useCallback, useMemo, useState, useEffect, useRef, useContext } from 'react';
import { useMedia } from 'react-use';
import { BREAKPOINTS } from '../../constants/layout.mjs';
import { useIsTouchDevice } from '../../hooks/useIsTouchDevice.mjs';
import { useLockBodyScrollCustom } from '../../hooks/useLockBodyScrollCustom.mjs';
import { checkUnreachable } from '../../utils/errors.mjs';
import { DEFAULT_STORE_ID, LONG_LANGUAGES, DEFAULT_LANGUAGE, SiteId, GENERIC_SITE_IDS, NAVIGATION_MAPPING } from './mapping.mjs';
import { ModalId } from './types.mjs';
import { getGlobalSiteConfig, isPartOfUrlPathname, parseSiteUrl, findFirstPossibleDeepNestedItemUrl, getSubItemUrl, getLanguageDirection } from './utils.mjs';
"use strict";
const MenuContext = createContext(null);
const MenuContextProvider = ({
  context,
  pathname: currentPathname = "/",
  storeId = DEFAULT_STORE_ID,
  hasSsrCapability = false,
  hash: currentHash = "",
  language = DEFAULT_LANGUAGE,
  globalSiteConfig,
  children
}) => {
  const isMobile = useMedia(BREAKPOINTS.TOP_NAV_MOBILE_AND_DOWN, false
  // Fallback value for SSR
  );
  const getSiteConfig = useCallback((siteId, storeIdOverride) => getGlobalSiteConfig(globalSiteConfig, siteId, storeIdOverride ?? storeId), [globalSiteConfig, storeId]);
  const currentSiteConfig = useMemo(
  // Generic site doesn't have a site config
  () => getSiteConfig(context), [context, getSiteConfig]);
  const deployedLanguages = useMemo(() => {
    return currentSiteConfig.languages ?? [
      /** Left empty as that indicates given site doesn't implement multiple language AT ALL. For example store would
       * have single language for now, but UISP doesn't have at all.
       */
    ];
  }, [currentSiteConfig.languages]);
  const isMultiLanguage = useMemo(() => {
    return deployedLanguages.length > 1;
  }, [deployedLanguages]);
  const isLongLanguage = useMemo(() => LONG_LANGUAGES.includes(language), [language]);
  const [isSsr, setIsSsr] = useState(hasSsrCapability);
  useEffect(() => {
    setIsSsr(false);
  }, []);
  const storeBaseUrl = useMemo(() => getSiteConfig(SiteId.Store).baseUrl, [getSiteConfig]);
  const [motif, setMotif] = useState("light");
  const handleSetMotif = useCallback(motif2 => {
    if (["light", "dark"].includes(motif2)) {
      setMotif(motif2);
    }
  }, []);
  const isMicrositeContext = useMemo(() => {
    switch (context) {
      case null:
      case void 0:
      case SiteId.Ui:
      case SiteId.Uisp:
      case SiteId.ExpertsUi:
      case SiteId.ExpertsUisp:
      case SiteId.Uwc:
      case SiteId.TechSpecs:
      case SiteId.Blog:
      case SiteId.CaseStudies:
      case SiteId.Installations:
      case SiteId.Creators:
      case SiteId.Generic:
        {
          return true;
        }
      case SiteId.Amplifi:
      case SiteId.Store:
        {
          return false;
        }
      default:
        {
          checkUnreachable(context);
          return true;
        }
    }
  }, [context]);
  const [{
    activeModal,
    prevActiveModal
  }, _setActiveModal] = useState({
    activeModal: null,
    prevActiveModal: null
  });
  const setActiveModal = useCallback(id => {
    _setActiveModal({
      activeModal: id,
      prevActiveModal: activeModal
    });
  }, [activeModal]);
  useEffect(() => {
    if (!isMobile && activeModal && [ModalId.MobileMenu, ModalId.ProMobileMenu].includes(activeModal)) {
      setActiveModal(null);
    }
  }, [activeModal, isMobile, setActiveModal]);
  useLockBodyScrollCustom(!!activeModal && [ModalId.MobileMenu, ModalId.ProMobileMenu].includes(activeModal));
  const currentCollectionPath = useMemo(() => {
    const url = new URL(currentPathname, storeBaseUrl);
    const matches = /(\/collections\/[\w-]+)/i.exec(url.pathname);
    return matches?.[1];
  }, [storeBaseUrl, currentPathname]);
  const currentProductPath = useMemo(() => {
    const url = new URL(currentPathname, storeBaseUrl);
    const matches = /((\/collections\/.+)?\/products\/[\w-]+)/i.exec(url.pathname);
    return matches?.[1];
  }, [storeBaseUrl, currentPathname]);
  const isPathnameActive = useCallback(pathname => {
    if (GENERIC_SITE_IDS.includes(context)) {
      return false;
    }
    if (isMicrositeContext) {
      const optional = pathname === "/" ? "?" : "";
      const micrositeRegExp = new RegExp(`^(?:/\\w{2})?${pathname}${optional}$`);
      return micrositeRegExp.test(currentPathname);
    }
    return !!currentCollectionPath && isPartOfUrlPathname(pathname, currentCollectionPath) || !!currentProductPath && isPartOfUrlPathname(pathname, currentProductPath);
  }, [context, isMicrositeContext, currentCollectionPath, currentProductPath, currentPathname]);
  const isSiteUrlActive = useCallback(url => {
    return isMicrositeContext &&
    // While SiteId is considered a microsite we don't want url matching for it
    !GENERIC_SITE_IDS.includes(context) && url && url[0] !== SiteId.Store && url[0] === context && isPartOfUrlPathname(currentPathname, url[1]) && (
    // If there's a hash in the SiteUrl, it should match the current hash for the item to be active
    !url[3] || currentHash.replace(/^#/, "") === url[3]);
  }, [context, currentHash, currentPathname, isMicrositeContext]);
  const createSiteUrl = useCallback(siteUrl => {
    const addSiteConfigReferenceSearchParams = !GENERIC_SITE_IDS.includes(context);
    const [siteId] = siteUrl;
    const targetSiteConfig = getSiteConfig(siteId);
    return parseSiteUrl({
      siteUrl,
      storeId,
      addSiteConfigReferenceSearchParams,
      language,
      targetSiteConfig,
      context
    });
  }, [context, getSiteConfig, storeId, language]);
  const parseSubSubRecursiveItems = useCallback(subSubItem => {
    const nestedSubSubItems = "items" in subSubItem && subSubItem.items ? subSubItem.items.map(parseSubSubRecursiveItems).filter(el => !!el) : [];
    const url = isMicrositeContext && subSubItem.url ? createSiteUrl(subSubItem.url) :
    // TODO: maybe need to improve to get first "available" child not the first form array, not sure if the
    // parseItemsFn filters them already out
    findFirstPossibleDeepNestedItemUrl(nestedSubSubItems) ?? (subSubItem.url && createSiteUrl(subSubItem.url));
    if (!url) {
      return null;
    }
    const active = subSubItem.url && isSiteUrlActive(subSubItem.url) ||
    // Any child item is active
    nestedSubSubItems.some(el => el.active);
    return {
      id: subSubItem.id,
      label: subSubItem.label,
      // subLabel: subSubItem.subLabel,
      icon: subSubItem.icon,
      mobileIcon: subSubItem.mobileIcon,
      isNew: subSubItem.isNew,
      active,
      url,
      possible: "items" in subSubItem ? nestedSubSubItems.some(item => item.possible) :
      // If gotten this far fallback to true
      true,
      items: nestedSubSubItems
    };
  }, [isMicrositeContext, createSiteUrl, isSiteUrlActive]);
  const parseSubSubMenuItems = useCallback(subItem => {
    return ("items" in subItem ? subItem.items : []).map(parseSubSubRecursiveItems).filter(el => !!el);
  }, [parseSubSubRecursiveItems]);
  const parseSubMenuItem = useCallback(subItem => {
    const subItemUrl = getSubItemUrl(subItem, isSiteUrlActive);
    const url = subItemUrl ? new URL(createSiteUrl(subItemUrl)) : null;
    if (!url) {
      return null;
    }
    const subSubItems = parseSubSubMenuItems(subItem);
    const subItemActive =
    // Site url is active
    subItem.url && isSiteUrlActive(subItem.url) ||
    // Any child item is active
    subSubItems.some(el => el.active);
    return {
      id: subItem.id,
      label: subItem.label,
      subLabel: subItem.subLabel,
      subLabelTheme: subItem.subLabelTheme,
      subLabelCollapseMode: subItem.subLabelCollapseMode,
      isNew: subItem.isNew,
      icon: subItem.icon,
      mobileIcon: subItem.mobileIcon,
      invisible: subItem.invisible,
      url: url.toString(),
      active: subItemActive,
      possible: subSubItems.length === 0 || subSubItems.some(item => item.possible),
      items: subSubItems
    };
  }, [isSiteUrlActive, createSiteUrl, parseSubSubMenuItems]);
  const parseMenuItem = useCallback(item => {
    const subItems = item.items.map(parseSubMenuItem).filter(subItem => !!subItem);
    let itemActive = false;
    if (isMicrositeContext &&
    // While SiteId is considered a microsite we don't want url matching for it
    !GENERIC_SITE_IDS.includes(context) && item.url && item.url[0] === context && item.url[0] !== SiteId.Store && isPartOfUrlPathname(currentPathname, item.url[1])) {
      itemActive = true;
    }
    const url = isMicrositeContext && item.url ? createSiteUrl(item.url) :
    // TODO: maybe need to improve to get first "available" child not the first form array, not sure if the
    // parseItemsFn filters them already out
    findFirstPossibleDeepNestedItemUrl(subItems) ?? (item.url && createSiteUrl(item.url) || item.fallbackUrl && createSiteUrl(item.fallbackUrl) || storeBaseUrl.toString());
    return {
      ...item,
      items: subItems,
      url,
      active: itemActive || subItems.some(subItem => subItem.active),
      possible: subItems.length === 0 || subItems.some(item2 => item2.possible)
    };
  }, [parseSubMenuItem, isMicrositeContext, context, currentPathname, createSiteUrl, storeBaseUrl]);
  const parseItemsFn = useCallback(items => items.map(parseMenuItem), [parseMenuItem]);
  const parseSection = useCallback(sectionType => {
    const section = NAVIGATION_MAPPING[sectionType];
    const parsedItems = parseItemsFn(section.items ?? []);
    const url = section.url ? createSiteUrl(section.url) : null;
    const finalUrl = url ||
    // TODO: maybe need to improve to get first "available" child not the first form array, not sure if the
    // parseItemsFn filters them already out
    findFirstPossibleDeepNestedItemUrl(parsedItems) || !section.url && createSiteUrl(section.fallbackUrl) || storeBaseUrl.toString();
    return {
      label: section.label,
      type: sectionType,
      url: finalUrl,
      active: parsedItems.some(({
        active
      }) => active),
      possible: parsedItems.length === 0 || parsedItems.some(item => item.possible),
      items: parsedItems
    };
  }, [createSiteUrl, storeBaseUrl, parseItemsFn]);
  const navigation = useMemo(() => {
    const primary = parseSection("primary");
    const secondary = parseSection("secondary");
    if (secondary.active) {
      primary.active = false;
    } else if (primary.active) {
      secondary.active = false;
    }
    return {
      primary,
      secondary
    };
  }, [parseSection]);
  const activeNavigationSectionType = useMemo(() => {
    if (navigation.primary.active) {
      return "primary";
    }
    if (navigation.secondary.active) {
      return "secondary";
    }
    return [SiteId.Uisp, SiteId.ExpertsUisp].includes(context) ? "secondary" : "primary";
  }, [context, navigation.primary.active, navigation.secondary.active]);
  const activeNavigationSection = useMemo(() => {
    return navigation[activeNavigationSectionType];
  }, [activeNavigationSectionType, navigation]);
  const inactiveNavigationSection = useMemo(() => {
    const inactiveType = activeNavigationSectionType === "primary" ? "secondary" : "primary";
    return navigation[inactiveType];
  }, [activeNavigationSectionType, navigation]);
  const motifFinal = useMemo(() => {
    if (activeModal === ModalId.MobileMenu) {
      return "dark";
    }
    return motif;
  }, [activeModal, motif]);
  const [navigationOutOfView, setNavigationOutOfView] = useState(
  // Default ref.current is null so by default it's not visible
  true);
  const isTouchDevice = useIsTouchDevice();
  const [showMobileEmptyCartNotification, setShowMobileEmptyCartNotification] = useState(false);
  const notificationTimeoutRef = useRef(null);
  const handleShowMobileEmptyCartNotification = useCallback(() => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setShowMobileEmptyCartNotification(true);
    notificationTimeoutRef.current = setTimeout(() => {
      setShowMobileEmptyCartNotification(false);
    }, 2e3);
  }, [setShowMobileEmptyCartNotification]);
  useEffect(() => {
    const handleScroll = () => setShowMobileEmptyCartNotification(false);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, []);
  const handleCartIconClick = useCallback((hasCartItems, navigateToCartFn) => {
    if (!hasCartItems) {
      if (isMobile) {
        handleShowMobileEmptyCartNotification();
      }
      return;
    }
    if (isTouchDevice || isMobile) {
      setActiveModal(ModalId.CartPreview);
    } else {
      navigateToCartFn();
    }
  }, [setActiveModal, isMobile, isTouchDevice, handleShowMobileEmptyCartNotification]);
  const value = useMemo(() => ({
    // Unrelated util to help aid with SSR in various places in app
    isSsr,
    storeId,
    motif: motifFinal,
    setMotif: handleSetMotif,
    context,
    isMicrositeContext,
    activeModal,
    prevActiveModal,
    setActiveModal,
    deployedLanguages,
    isMultiLanguage,
    language,
    languageDirection: getLanguageDirection(language),
    isLongLanguage,
    navigation,
    activeNavigationSection,
    inactiveNavigationSection,
    isPathnameActive,
    navigationOutOfView,
    setNavigationOutOfView,
    createSiteUrl,
    handleCartIconClick,
    showMobileEmptyCartNotification
  }), [activeModal, activeNavigationSection, context, createSiteUrl, deployedLanguages, handleSetMotif, inactiveNavigationSection, isLongLanguage, isMicrositeContext, isMultiLanguage, isPathnameActive, isSsr, language, motifFinal, navigation, navigationOutOfView, prevActiveModal, setActiveModal, storeId, handleCartIconClick, showMobileEmptyCartNotification]);
  return /* @__PURE__ */jsx(MenuContext.Provider, {
    value,
    children
  });
};
function useMenu() {
  const context = useContext(MenuContext);
  if (context === null) {
    throw new Error("useMenu must be used within a MenuContextProvider");
  }
  return context;
}
export { MenuContext, MenuContextProvider, useMenu };
//# sourceMappingURL=context.mjs.map
